<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useFacilityStore } from '@/stores/facility.js';
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { useServerTable } from '@/composables/useServerTable.js';
import { fancyDate } from '@/filters/fancyDate.js';


const props = defineProps({
  search: { type: String, default: '' },
  status: { type: String, default: 'ACTIVE' },
  customer: { type: [String, Number], default: undefined }, // ВАЖНО: фильтр по клиенту
  responsible: { type: [String, Number], default: undefined },

});

const emit = defineEmits(['initial-count-loaded']);
const initialLoadEmitted = ref(false);

const facilityStore = useFacilityStore();
const router = useRouter();

const {
  data: facilities,
  totalRecords,
  loading,
  rowsPerPage,
  currentPage,
  sortField,
  sortOrder,
  setParams,
  load,
  setSearch,
  setExtra,
  onPageChange,
  onSort,
  firstIndex
} = useServerTable({
  initialRows: 15,
  debounceMs: 800,
  storageKey: 'table.facilities.pageSize',
  loadPage: async ({ page, rows, search, ...extra }) => {
    return await facilityStore.loadFacilities({
      page,
      page_size: rows,
      search,
      status: extra.status, // 'ACTIVE' | 'ARCHIVED'
      customer: extra.customer, // пробрасываем customer дальше в стор
      ordering: extra.ordering,
      responsible: extra.responsible
    });
  }
});


const goToFacility = async (event) => {
  if (event.data && event.data.id) {
    await router.push(`/facilities/${event.data.id}`);
  }
};

watch(
    () => [props.search, props.status, props.customer, props.responsible],
    (newVals, oldVals) => {
      const isInitialLoad = oldVals === undefined;
      const [search, status, customer, responsible] = newVals || [];

      if (!isInitialLoad) {
        const [prevSearch, prevStatus, prevCustomer, prevResponsible] = oldVals || [];
        if (search === prevSearch && status === prevStatus && customer === prevCustomer && responsible === prevResponsible) {
          return;
        }
      }

      setParams({
        search: (search || '').trim(),
        extra: { status, customer, responsible },
        resetPage: true,
        immediate: isInitialLoad,
      });
    },
    { immediate: true }
);

// Эмитим count после первой загрузки
watch(totalRecords, (count) => {
  if (!initialLoadEmitted.value && count !== null) {
    initialLoadEmitted.value = true;
    emit('initial-count-loaded', { count, status: props.status });
  }
});

</script>

<template>
  <DataTable
      :value="facilities"
      stripedRows
      lazy
      paginator
      scrollable
      scrollHeight="flex"
      :rows="rowsPerPage"
      :rowsPerPageOptions="[10, 20, 30]"
      :totalRecords="totalRecords"
      :loading="loading"
      :first="firstIndex()"
      :sortField="sortField"
      :sortOrder="sortOrder"
      @page="onPageChange"
      @sort="onSort"
      class="cursor-pointer"
      @row-click="goToFacility"
  >
    <Column
        field="short_name"
        header="Название"
        sortable
        :headerClass="'w-full lg:w-[70%]'"
        :bodyClass="'w-full lg:w-[70%]'"
    />
    <Column
        field="works_count"
        header="Работы"
        sortable
        style="width: 5%"
        :headerClass="'hidden lg:table-cell'"
        :bodyClass="'hidden lg:table-cell'"
    />
    <Column
        field="term_end"
        header="Окончание"
        sortable
        style="width: 5%"
        :headerClass="'hidden lg:table-cell'"
        :bodyClass="'hidden lg:table-cell'"
    >
      <template #body="{ data }">
        {{ fancyDate(data.term_end, 'short') }}
      </template>
    </Column>
    <Column
        field="primary_responsible_full_name"
        header="Ответственный"
        sortable
        style="width: 20%"
        :headerClass="'hidden lg:table-cell'"
        :bodyClass="'hidden lg:table-cell'"
    >
      <template #body="{ data }">
        {{ data.primary_responsible_full_name }}
      </template>
    </Column>

  </DataTable>
</template>

<style scoped />
